import { useFormikContext } from "formik";
import Callout from "../../UI/Callout";
import {
  getLastAnniversaryDate,
  getNewLoyerFromIndice,
  getNextAnniversaryDate,
} from "../../../utils/index.jsx";
import TextInput from "../../UI/TextInput";
import moment from "moment/moment";
import useIrls from "../../../hooks/use-irls";
import Select from "../../UI/Select";
import { currencyFormatter } from "../../../api/Functions";
import { useCallback, useEffect, useState } from "react";
import { ReactSelectStandalone } from "../../UI/SelectStandalone.jsx";

export default function RevisionForm({ tenant, property }) {
  const { irls } = useIrls();
  const { values, setFieldValue } = useFormikContext();
  const [initialYears, setInitialYears] = useState({
    lastYear: null,
    newYear: null,
  });

  const irlTrimestre = tenant?.irl_trimestre;
  const dateDebut = tenant?.debut;

  const nextAnniversaryDate = getNextAnniversaryDate(dateDebut);
  const startOfMonthBeforeAnniversaryMonth = nextAnniversaryDate
    .clone()
    .subtract(1, "months")
    .startOf("month");
  const now = moment();
  // Revision possible si on est entre le 1er du mois précédent la date anniversaire et la date anniversaire
  const isForFuture = now.isBetween(
    startOfMonthBeforeAnniversaryMonth,
    nextAnniversaryDate,
  );

  const findIndice = useCallback(
    (annee, trimestre) =>
      irls?.find(
        (irl) =>
          irl.annee === annee &&
          irl.trimestre === trimestre &&
          irl.region === tenant.type_irl,
      )?.indice ?? "",
    [irls, tenant.type_irl],
  );

  const getIndice = useCallback(
    (t) => {
      const trimestre = parseInt(t);

      let previousIndiceYear;
      const lastAnniversary = getLastAnniversaryDate(dateDebut);
      if (lastAnniversary.quarter() > trimestre) {
        previousIndiceYear = lastAnniversary.year() - 1;
      } else {
        previousIndiceYear = lastAnniversary.year() - 2;
      }
      if (isForFuture) {
        previousIndiceYear++;
      }

      const newIndiceYear = previousIndiceYear + 1;

      return {
        previousIndiceYear,
        newIndiceYear,
        previousIndice: findIndice(previousIndiceYear, trimestre),
        newIndice: findIndice(newIndiceYear, trimestre),
      };
    },
    [dateDebut, isForFuture, findIndice],
  );

  const setForm = useCallback(
    (t) => {
      const { previousIndiceYear, newIndiceYear, previousIndice, newIndice } =
        getIndice(t);
      setFieldValue("irl_initial_trimestre", parseInt(t));
      setFieldValue("irl_last_year_trimestre", `T${t}`);
      setFieldValue("irl_last_year_annee", previousIndiceYear);
      setFieldValue("irl_last_year_indice", previousIndice);
      setFieldValue("irl_new_trimestre", `T${t}`);
      setFieldValue("irl_new_annee", newIndiceYear);
      setFieldValue("irl_new_indice", newIndice);
      setInitialYears({
        lastYear: previousIndiceYear,
        newYear: newIndiceYear,
      });
    },
    [getIndice, setFieldValue],
  );

  const currentYear = moment().year();
  const newYearOptions = Array.from({ length: 3 }, (_, i) => ({
    value: currentYear - 2 + i,
  }));
  const lastYearOptions = newYearOptions.map((o) => ({ value: o.value - 1 }));

  const updateLastYear = useCallback(
    (year) => {
      const trimestre = parseInt(values.irl_initial_trimestre);
      if (!trimestre || !year) return;
      const newYear = year + 1;
      const lastIndice = findIndice(year, trimestre);
      const newIndice = findIndice(newYear, trimestre);

      setFieldValue("irl_last_year_annee", year);
      setFieldValue("irl_last_year_indice", lastIndice);
      setFieldValue("irl_new_annee", newYear);
      setFieldValue("irl_new_indice", newIndice);
    },
    [values.irl_initial_trimestre, findIndice, setFieldValue],
  );

  useEffect(() => {
    if (irlTrimestre) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setForm(irlTrimestre);
    }
  }, [setForm, irlTrimestre]);

  if (!tenant) return null;

  let revisionStatus;

  if (moment().isBefore(tenant.last_revision_date)) {
    revisionStatus = (
      <Callout variant="danger">
        Vous avez déjà fait une révision de loyer pour cette année.
        <br />
        Celle si sera applicable à partir du{" "}
        {moment(tenant.last_revision_date).format("DD MMMM YYYY")}
      </Callout>
    );
  } else if (isForFuture) {
    revisionStatus = (
      <Callout variant="success">
        La date anniversaire du bail arrive ce mois ci ou le prochain.
        <br /> Vous pouvez dès à présent envoyer le courrier de révision de
        loyer. <br /> Il sera applicable dès le mois prochain.
      </Callout>
    );
  } else if (moment().diff(tenant.debut, "years") === 0) {
    revisionStatus = (
      <Callout variant="danger">
        Le locataire est arrivé il y a moins d'un an.
        <br />
        Vous n'avez légalement pas le droit de faire une révision de loyer
      </Callout>
    );
  } else if (
    moment(tenant.last_revision_date).isBetween(
      getLastAnniversaryDate(tenant.debut),
      moment(),
      undefined,
      "[]",
    )
  ) {
    revisionStatus = (
      <Callout variant="danger">
        Vous avez déjà fait une révision de loyer sur l'année écoulée.
        <br />
        Vous n'avez légalement pas le droit de faire une révision de loyer.
        <br />
        Prochaine révision possible :{" "}
        {getNextAnniversaryDate(tenant.debut).format("DD MMMM YYYY")}
      </Callout>
    );
  }

  function getLoyerAfterRevision(tenant, values) {
    let loyer_hc = 0;
    let charges = 0;

    if (tenant.type_charge === "provision") {
      loyer_hc = getNewLoyerFromIndice(
        tenant.loyer_hc,
        values.irl_last_year_indice,
        values.irl_new_indice,
      );
      return ` ${currencyFormatter(loyer_hc)} hors charge`;
    } else {
      loyer_hc = getNewLoyerFromIndice(
        tenant.loyer_hc,
        values.irl_last_year_indice,
        values.irl_new_indice,
      );
      charges = getNewLoyerFromIndice(
        tenant.charges,
        values.irl_last_year_indice,
        values.irl_new_indice,
      );
      return ` ${currencyFormatter(loyer_hc + charges)} (${currencyFormatter(
        loyer_hc,
      )} hors charge + ${currencyFormatter(charges)} de charges)`;
    }
  }

  return (
    <>
      <h2 className={"margin0 text-center"}>Faire une révision de loyer</h2>
      <p className={"text-center marginT10"}>
        Loyer en cours à la date de révision :{" "}
        {tenant.type_charge === "provision" ? (
          <>{currencyFormatter(tenant.loyer_hc)} hors charge</>
        ) : (
          <>
            {currencyFormatter(tenant.loyer_hc + tenant.charges)} (
            {currencyFormatter(tenant.loyer_hc)} +{" "}
            {currencyFormatter(tenant.charges)} de charges)
          </>
        )}
      </p>
      <p className={"marginT10 text-center marginB10"}>
        Date de la dernière révision de loyer :{" "}
        {tenant.last_revision_date
          ? moment(tenant.last_revision_date).format("DD MMMM YYYY")
          : "jamais"}
      </p>
      {initialYears.lastYear !== null &&
        initialYears.newYear !== null &&
        (values.irl_last_year_annee !== initialYears.lastYear ||
          values.irl_new_annee !== initialYears.newYear) && (
          <Callout variant="warning">
            Qalimo sélectionne par défaut la bonne année de révision. Si vous
            devez la changer, c'est que vous n'avez sans doute pas sélectionné
            le bon trimestre lors de la génération du bail.
          </Callout>
        )}
      {(property.indice_dpe === "G" || property.indice_dpe === "F") && (
        <Callout variant="danger">
          Votre logement à un indice DPE F ou G. Il n'est pas légal de réaliser
          une augmentation de loyer
        </Callout>
      )}
      {tenant.is_triennal && (
        <Callout variant="warning">
          Il s'agit d'un bail avec révision de loyer triennale. Qalimo ne gère
          pas encore ce genre de révision.
        </Callout>
      )}
      {revisionStatus}
      <p className={"marginT10"}>
        Trimestre de révision du loyer ({tenant.type_irl})
      </p>
      <div className={"flex"}>
        <Select
          name="irl_initial_trimestre"
          className={"marginT0 marginR10"}
          onChange={(e) => setForm(e.target.value)}
        >
          <option value="">Sélectionnez un trimestre</option>
          <option value="1">Trimestre 1</option>
          <option value="2">Trimestre 2</option>
          <option value="3">Trimestre 3</option>
          <option value="4">Trimestre 4</option>
        </Select>
      </div>
      {values.irl_new_indice ? (
        <>
          <p>Indice de l'année précédente</p>
          <div className={"flex"}>
            <TextInput
              type="text"
              name="irl_last_year_trimestre"
              placeholder={"Trimestre"}
              className={"marginR10"}
              disabled
            />
            <ReactSelectStandalone
              placeholder="Année"
              className="marginR10"
              options={lastYearOptions}
              getOptionLabel={(o) => o.value.toString()}
              value={{ value: values.irl_last_year_annee }}
              onChange={(o) => updateLastYear(o.value)}
              isClearable={false}
            />
            <TextInput
              type="text"
              name="irl_last_year_indice"
              placeholder={"Indice"}
              disabled
            />
          </div>
          <p>Nouvel indice applicable</p>
          <div className={"flex"}>
            <TextInput
              type="text"
              name="irl_new_trimestre"
              placeholder={"Trimestre"}
              className={"marginR10"}
              disabled
            />
            <ReactSelectStandalone
              placeholder="Année"
              className="marginR10"
              options={newYearOptions}
              getOptionLabel={(o) => o.value.toString()}
              value={{ value: values.irl_new_annee }}
              onChange={(o) => updateLastYear(o.value - 1)}
              isClearable={false}
            />
            <TextInput
              type="text"
              name="irl_new_indice"
              placeholder={"Indice"}
              disabled
            />
          </div>

          <p className={"primaryText text-center"}>
            Le loyer après la révision sera de
            {getLoyerAfterRevision(tenant, values)}
          </p>
          <p className={"text-center secondaryText marginT10"}>
            Attention ce montant ne sera dû qu’à compter du prochain loyer
            suivant la demande de révision.
          </p>
        </>
      ) : (
        values.irl_initial_trimestre && (
          <Callout variant="danger">
            L'indice que vous cherchez n'est pas encore sortit :
            <ul>
              <li>
                Indice du trimestre {values.irl_new_trimestre}{" "}
                {values.irl_new_annee}
              </li>
            </ul>
          </Callout>
        )
      )}
    </>
  );
}
